<script>
  import { useForm } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';
  import AppLayout from '../../components/AppLayout.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Button from '../../components/ui/Button.svelte';
  import PlatformBadge from '../../components/ui/PlatformBadge.svelte';

  let { calendars = [] } = $props();

  const DAYS = [
    { value: 0, label: 'Sun' }, { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' }, { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  ];

  const form = useForm({
    name: '',
    calendar_id: calendars[0]?.id ?? null,
    mode: 'manual',
    time_of_day: '09:00',
    days_of_week: [],
    social_account_ids: [],
    bin_ids: [],
    active: true,
  });

  let selectedCalendar = $derived(calendars.find((c) => c.id === form.calendar_id) ?? null);
  let social_accounts = $derived(selectedCalendar?.social_accounts ?? []);
  let bins = $derived(selectedCalendar?.bins ?? []);

  function selectCalendar(id) {
    form.calendar_id = id;
    form.social_account_ids = [];
    form.bin_ids = [];
  }

  function toggleDay(day) {
    form.days_of_week = form.days_of_week.includes(day)
      ? form.days_of_week.filter((d) => d !== day)
      : [...form.days_of_week, day];
  }

  function toggleAccount(id) {
    form.social_account_ids = form.social_account_ids.includes(id)
      ? form.social_account_ids.filter((x) => x !== id)
      : [...form.social_account_ids, id];
  }

  function toggleBin(id) {
    form.bin_ids = form.bin_ids.includes(id)
      ? form.bin_ids.filter((x) => x !== id)
      : [...form.bin_ids, id];
  }

  function moveBin(id, direction) {
    const ids = [...form.bin_ids];
    const index = ids.indexOf(id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    form.bin_ids = ids;
  }

  function submit() {
    form.transform((data) => ({ slot: data })).post('/slots');
  }
</script>

<AppLayout title="New slot">
  {#if calendars.length === 0}
    <Card>
      <div class="text-center py-6">
        <p class="text-secondary-500 mb-4">Create a calendar before adding a slot.</p>
        <Button href="/calendars/new">Create a calendar</Button>
      </div>
    </Card>
  {:else}
    <div class="max-w-2xl">
      <Card>
        <form onsubmit={(e) => { e.preventDefault(); submit(); }} class="flex flex-col gap-5">
          <Field label="Calendar" id="calendar" error={form.errors.calendar_id}>
            {#snippet children(describedBy)}
              <select id="calendar" class="input" value={form.calendar_id} onchange={(e) => selectCalendar(Number(e.target.value))} aria-describedby={describedBy}>
                {#each calendars as cal}
                  <option value={cal.id}>{cal.name}</option>
                {/each}
              </select>
            {/snippet}
          </Field>

          <Field label="Name" id="name" error={form.errors.name}>
            {#snippet children(describedBy)}
              <input id="name" type="text" class="input" bind:value={form.name} placeholder="e.g. Weekday morning post" required aria-describedby={describedBy} />
            {/snippet}
          </Field>

          <Field label="Days" id="days" error={form.errors.days_of_week}>
            <div class="flex gap-2">
              {#each DAYS as day}
                <button
                  type="button"
                  onclick={() => toggleDay(day.value)}
                  class="w-11 h-11 rounded-lg border text-xs font-medium transition-colors
                    {form.days_of_week.includes(day.value) ? 'bg-primary-900 text-white border-primary-900' : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'}"
                >
                  {day.label}
                </button>
              {/each}
            </div>
          </Field>

          <Field label="Time" id="time_of_day" error={form.errors.time_of_day}>
            {#snippet children(describedBy)}
              <input id="time_of_day" type="time" class="input" bind:value={form.time_of_day} required aria-describedby={describedBy} />
            {/snippet}
          </Field>

          <Field label="Post to" id="accounts" error={form.errors.social_account_ids}>
            {#if social_accounts.length === 0}
              <p class="text-sm text-secondary-500">This calendar has no connected accounts yet.</p>
            {:else}
              <div class="flex flex-col gap-2">
                {#each social_accounts as acc}
                  <label class="flex items-center gap-3 px-3 py-2 rounded-xl border border-secondary-200 cursor-pointer hover:bg-secondary-50 text-sm">
                    <input type="checkbox" checked={form.social_account_ids.includes(acc.id)} onchange={() => toggleAccount(acc.id)} />
                    <PlatformBadge platform={acc.platform} handle={acc.handle} />
                  </label>
                {/each}
              </div>
            {/if}
          </Field>

          <Field label="How should this slot get filled?" id="mode" error={form.errors.mode}>
            <div class="flex flex-col gap-2">
              <label class="flex items-center gap-3 px-3 py-2 rounded-xl border border-secondary-200 cursor-pointer hover:bg-secondary-50 text-sm">
                <input type="radio" name="mode" value="manual" checked={form.mode === 'manual'} onchange={() => { form.mode = 'manual'; }} />
                Manual — I'll pick a post myself when it's due
              </label>
              <label class="flex items-center gap-3 px-3 py-2 rounded-xl border border-secondary-200 cursor-pointer hover:bg-secondary-50 text-sm">
                <input type="radio" name="mode" value="automatic" checked={form.mode === 'automatic'} onchange={() => { form.mode = 'automatic'; }} />
                Automatic — pull the next post from a bin
              </label>
            </div>
          </Field>

          {#if form.mode === 'automatic'}
            <Field label="Bins, in priority order" id="bins" error={form.errors.bin_ids}>
              {#if bins.length === 0}
                <p class="text-sm text-secondary-500">This calendar has no bins yet.</p>
              {:else}
                <div class="flex flex-col gap-2">
                  {#each bins as bin}
                    <label class="flex items-center gap-3 px-3 py-2 rounded-xl border border-secondary-200 cursor-pointer hover:bg-secondary-50 text-sm">
                      <input type="checkbox" checked={form.bin_ids.includes(bin.id)} onchange={() => toggleBin(bin.id)} />
                      {bin.name}
                    </label>
                  {/each}
                </div>
                {#if form.bin_ids.length > 1}
                  <div class="flex flex-col gap-1 mt-2">
                    <span class="text-xs text-secondary-400">Order (tried top to bottom):</span>
                    {#each form.bin_ids as id, i}
                      {@const bin = bins.find((b) => b.id === id)}
                      <div class="flex items-center justify-between text-xs text-secondary-600 px-2 py-1">
                        <span>{i + 1}. {bin?.name}</span>
                        <div class="flex gap-1">
                          <button type="button" onclick={() => moveBin(id, -1)} disabled={i === 0} class="p-1 disabled:opacity-30"><Icon icon="mdi:arrow-up" /></button>
                          <button type="button" onclick={() => moveBin(id, 1)} disabled={i === form.bin_ids.length - 1} class="p-1 disabled:opacity-30"><Icon icon="mdi:arrow-down" /></button>
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              {/if}
            </Field>
          {/if}

          <div class="flex items-center gap-3">
            <Button type="submit" disabled={form.processing}>{form.processing ? 'Creating…' : 'Create slot'}</Button>
            <Button href="/slots" variant="ghost">Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  {/if}
</AppLayout>
